import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import '../components/CommonButton.css';
import { useNavigate } from 'react-router-dom';
import { saveTeams, saveMatches } from '../utils/storage';
import DraggableList from '../components/DraggableList';
import { generateAssaltiFederali } from '../utils/fencingLogic';
import { generateZigZagTeamMatches } from '../utils/sequenceGenerator';
import UserMinusIcon from '../components/UserMinusIcon';
import UserPlusIcon from '../components/UserPlusIcon';
import ScrollToTopButton from '../components/ScrollToTopButton';
import {
  UsersIcon,
  FencingSwordsIcon,
  CustomSettingsIcon
} from '../components/FencingIcons';

// Funzione di utilità per aggiornare assalti nei match
function updateAssaltiInMatches(teams, teamIdx, stoccatePerAssaltoSetting) {
  const stoccatePerAssalto = parseInt(stoccatePerAssaltoSetting) || 5;
  // Aggiorna round robin
  let matches = JSON.parse(localStorage.getItem('matches') || '[]');
  let changed = false;
  matches = matches.map(m => {
    if ((m.teamA === teamIdx || m.teamB === teamIdx) && !m.completed) {
      const teamA = teams[m.teamA];
      const teamB = teams[m.teamB];
      const redMembers = Array.isArray(teamA?.members) ? teamA.members : [];
      const greenMembers = Array.isArray(teamB?.members) ? teamB.members : [];
      const assalti = generateAssaltiFederali(redMembers, greenMembers);
      m.assalti = assalti;
      m.numeroAssalti = assalti.length;
      m.stoccatePerAssalto = stoccatePerAssalto;
      m.stoccateTotali = m.numeroAssalti * m.stoccatePerAssalto;
      changed = true;
    }
    return m;
  });
  if (changed) localStorage.setItem('matches', JSON.stringify(matches));

  // Aggiorna bracket
  let bracket = JSON.parse(localStorage.getItem('bracket') || '[]');
  let bracketChanged = false;
  for (let round of bracket) {
    for (let match of round) {
      if (!match.completed && ((match.team1 && match.team1.id === teamIdx) || (match.team2 && match.team2.id === teamIdx))) {
        const teamA = teams[match.team1.id];
        const teamB = teams[match.team2.id];
        const redMembers = Array.isArray(teamA?.members) ? teamA.members : [];
        const greenMembers = Array.isArray(teamB?.members) ? teamB.members : [];
        const assalti = generateAssaltiFederali(redMembers, greenMembers);
        match.assalti = assalti;
        match.stoccatePerAssalto = stoccatePerAssalto;
        match.stoccateTotali = assalti.length * match.stoccatePerAssalto;
        bracketChanged = true;
      }
    }
  }
  if (bracketChanged) localStorage.setItem('bracket', JSON.stringify(bracket));
}

// Funzione per aggiornare tutti i dati torneo
// aggiornaDatiTorneo duplicata rimossa (usata quella interna al componente)

// Definizione stili condivisi per uniformità con IndividualCompetitionConfig
const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: '#f7fafd'
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 18,
    boxShadow: '0 4px 24px #0002',
    padding: 16,
    marginVertical: 12,
    borderWidth: 2,
    borderColor: '#1976d2',
    transition: 'box-shadow 0.2s',
    maxWidth: '100vw',
    overflowX: 'auto',
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  input: {
    borderWidth: 2,
    borderColor: '#b3c6e6',
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
    backgroundColor: '#f4faff',
    fontWeight: 'bold',
    fontSize: 16,
    transition: 'border-color 0.2s',
  },
  inputFocus: {
    borderColor: '#1976d2',
    backgroundColor: '#fff',
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 24,
    marginVertical: 8,
    fontWeight: 'bold',
    fontSize: 17,
    boxShadow: '0px 3px 4px rgba(25, 118, 210, 0.18)',
  },
  btnPrimary: {
    backgroundColor: '#1976d2',
    color: '#fff',
  },
  btnSecondary: {
    backgroundColor: '#388e3c',
    color: '#fff',
  },
  btnDanger: {
    backgroundColor: '#d32f2f',
    color: '#fff',
  },
  btnText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 17,
    textAlign: 'center',
  },
  membersTitle: {
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 4,
    color: '#388e3c',
    fontSize: 15
  },
});

const SetupWizard = () => {
  // Definizione tema colori (stile moderno)
  const theme = {
    primary: '#1976d2',
    secondary: '#388e3c',
    accent: '#ffe082',
    danger: '#d32f2f',
    bg: '#f7fafd',
    card: '#fff',
    border: '#e0e3ef',
    shadow: '0 2px 16px #0002',
  };

  const navigate = useNavigate();
  // Carica squadre da localStorage se presenti
  const [teams, setTeams] = useState(() => {
    const stored = localStorage.getItem('teams');
    if (stored) return JSON.parse(stored);
    return [
      { name: '', members: [{ name: '' }] },
      { name: '', members: [{ name: '' }] }
    ];
  });

  const [numTeams, setNumTeams] = useState(teams.length);
  const [stoccatePerAssalto, setStoccatePerAssalto] = useState(() => {
    const val = localStorage.getItem('stoccatePerAssalto');
    return val ? val : '5';
  });

  useEffect(() => {
    setNumTeams(teams.length);
  }, [teams.length]);

  useEffect(() => {
    localStorage.setItem('stoccatePerAssalto', stoccatePerAssalto);
    localStorage.setItem('teams', JSON.stringify(teams));
  }, [stoccatePerAssalto, teams]);

  useEffect(() => {
    teams.forEach((_, idx) => {
      updateAssaltiInMatches(teams, idx, stoccatePerAssalto);
    });
  }, [stoccatePerAssalto, teams]);

  // Funzione per aggiornare nome squadra
  const updateTeamName = (index, name) => {
    const updated = [...teams];
    updated[index].name = name;
    setTeams(updated);
    localStorage.setItem('teams', JSON.stringify(updated));
  };

  // Funzione per aggiornare i membri
  const updateTeamMember = (teamIdx, memberIdx, value) => {
    const updated = [...teams];
    updated[teamIdx].members[memberIdx] = { name: value };
    setTeams(updated);
    localStorage.setItem('teams', JSON.stringify(updated));
    updateAssaltiInMatches(updated, teamIdx, stoccatePerAssalto);
  };

  // Aggiungi/rimuovi membri
  const addMember = (teamIdx) => {
    const updated = [...teams];
    updated[teamIdx].members.push({ name: '' });
    setTeams(updated);
    localStorage.setItem('teams', JSON.stringify(updated));
    updateAssaltiInMatches(updated, teamIdx, stoccatePerAssalto);
  };
  const removeMember = (teamIdx, memberIdx) => {
    const updated = [...teams];
    updated[teamIdx].members.splice(memberIdx, 1);
    setTeams(updated);
    localStorage.setItem('teams', JSON.stringify(updated));
    updateAssaltiInMatches(updated, teamIdx, stoccatePerAssalto);
  };

  // Funzioni increment e decrement per valori numerici
  const increment = (setter, val, max = 99) => {
    const newVal = parseInt(val, 10) + 1;
    if (!isNaN(newVal) && newVal <= max) setter(newVal.toString());
  };

  const decrement = (setter, val, min = 0) => {
    const newVal = parseInt(val, 10) - 1;
    if (!isNaN(newVal) && newVal >= min) setter(newVal.toString());
  };

  // Funzione di utilità per aggiornare il numero di squadre
  const handleNumTeamsChange = useCallback((newNum) => {
    const num = parseInt(newNum, 10);
    if (isNaN(num) || num < 2) return;

    setNumTeams(num);

    // Se il numero di squadre è aumentato, aggiungiamo nuove squadre
    if (num > teams.length) {
      const newTeams = [...teams];
      for (let i = teams.length; i < num; i++) {
        newTeams.push({ name: '', members: [{ name: '' }] });
      }
      setTeams(newTeams);
    }
    // Se il numero di squadre è diminuito, rimuoviamo squadre
    else if (num < teams.length) {
      setTeams(teams.slice(0, num));
    }
  }, [teams]);

  // Genera i match secondo la nuova logica staffetta
  const generateRoundRobinMatches = useCallback((teams) => {
    const matches = [];
    const N = teams.length;
    // Recupera il valore di stoccatePerAssalto scelto dall'utente, se presente
    let stoccatePerAssalto = 5;
    try {
      const saved = localStorage.getItem('stoccatePerAssalto');
      if (saved) stoccatePerAssalto = parseInt(saved);
    } catch { }

    // Genera gli incontri con pattern zig-zag
    const teamMatches = generateZigZagTeamMatches(N);

    // Crea gli oggetti match per ogni coppia di squadre
    teamMatches.forEach(([i, j, invertLati]) => {
      // Se invertLati è true, invertiamo teamA e teamB
      const teamAIndex = invertLati ? j : i;
      const teamBIndex = invertLati ? i : j;

      const teamA = teams[teamAIndex];
      const teamB = teams[teamBIndex];
      const redMembers = Array.isArray(teamA?.members) ? teamA.members : [];
      const greenMembers = Array.isArray(teamB?.members) ? teamB.members : [];
      const assalti = generateAssaltiFederali(redMembers, greenMembers);
      matches.push({
        teamA: teamAIndex,
        teamB: teamBIndex,
        assalti,
        numeroAssalti: assalti.length,
        stoccatePerAssalto,
        stoccateTotali: assalti.length * stoccatePerAssalto,
        scoreA: 0,
        scoreB: 0,
        completed: false
      });
    });

    return matches;
  }, []);

  // Funzione per aggiornare i dati del torneo preservando i risultati degli assalti già disputati
  const aggiornaDatiTorneo = useCallback((teams, stoccatePerAssaltoSetting) => {
    const stoccatePerAssalto = parseInt(stoccatePerAssaltoSetting) || 5;

    // Recupera i match esistenti per conservare i risultati
    const oldMatches = JSON.parse(localStorage.getItem('matches') || '[]');
    if (!Array.isArray(oldMatches)) {
      alert('Nessun incontro trovato: Prima crea il torneo con il pulsante Crea Torneo.');
      return;
    }

    // Mappe per conservare i risultati degli incontri e assalti esistenti
    const completedMatches = {};
    const partialResults = {};

    // Salva i risultati degli incontri completati e parziali
    oldMatches.forEach(m => {
      const key = `${m.teamA}|${m.teamB}`;
      if (m.completed) {
        // Incontro completato, conserviamo tutto
        completedMatches[key] = m;
      } else if (m.assalti && m.assalti.some(a => a.scoreA > 0 || a.scoreB > 0)) {
        // Incontro con punteggi parziali, conserviamo gli assalti
        partialResults[key] = m.assalti;
      }
    });

    // GENERA COMPLETAMENTE NUOVI INCONTRI
    const newMatches = [];

    // Genera tutti gli incontri possibili
    for (let i = 0; i < teams.length; i++) {
      for (let j = i + 1; j < teams.length; j++) {
        const teamA = teams[i];
        const teamB = teams[j];

        // Genera gli assalti per questo incontro
        const assalti = generateAssaltiFederali(teamA, teamB, stoccatePerAssalto);

        // Chiave per cercare risultati esistenti
        const key = `${i}|${j}`;
        const invertedKey = `${j}|${i}`;

        // Verifica se questo incontro era già completato
        if (completedMatches[key]) {
          // Reimposta l'incontro già completato
          newMatches.push(completedMatches[key]);
          continue;
        } else if (completedMatches[invertedKey]) {
          // Reimposta l'incontro già completato (con lati invertiti)
          newMatches.push(completedMatches[invertedKey]);
          continue;
        }

        // Se l'incontro non era completato, crea un nuovo incontro
        let matchAssalti = assalti;
        let totalScoreA = 0;
        let totalScoreB = 0;

        // Recupera eventuali punteggi parziali
        if (partialResults[key] || partialResults[invertedKey]) {
          const oldAssalti = partialResults[key] || partialResults[invertedKey];

          // Merge dei punteggi parziali
          matchAssalti = assalti.map((a, idx) => {
            if (idx < oldAssalti.length) {
              // Se l'assalto aveva punteggi, li preserviamo
              if (oldAssalti[idx].completed) {
                totalScoreA += oldAssalti[idx].scoreA || 0;
                totalScoreB += oldAssalti[idx].scoreB || 0;
                return {
                  ...a,
                  scoreA: oldAssalti[idx].scoreA || 0,
                  scoreB: oldAssalti[idx].scoreB || 0,
                  completed: oldAssalti[idx].completed,
                  winner: oldAssalti[idx].winner,
                  notes: oldAssalti[idx].notes
                };
              } else if (oldAssalti[idx].scoreA > 0 || oldAssalti[idx].scoreB > 0) {
                totalScoreA += oldAssalti[idx].scoreA || 0;
                totalScoreB += oldAssalti[idx].scoreB || 0;
                return {
                  ...a,
                  scoreA: oldAssalti[idx].scoreA || 0,
                  scoreB: oldAssalti[idx].scoreB || 0
                };
              }
            }
            return a;
          });
        }

        // Aggiungi il nuovo incontro con eventuali punteggi preservati
        newMatches.push({
          teamA: i,
          teamB: j,
          teamAName: teamA.name || `Squadra ${i + 1}`,
          teamBName: teamB.name || `Squadra ${j + 1}`,
          assalti: matchAssalti,
          numeroAssalti: matchAssalti.length,
          stoccatePerAssalto,
          stoccateTotali: matchAssalti.length * stoccatePerAssalto,
          scoreA: totalScoreA,
          scoreB: totalScoreB,
          completed: false
        });
      }
    }

    console.log(`Generati ${newMatches.length} incontri per ${teams.length} squadre`);
    console.log(`Preservati ${Object.keys(completedMatches).length} incontri completati e ${Object.keys(partialResults).length} con punteggi parziali.`);

    // Salva i nuovi incontri
    localStorage.setItem('matches', JSON.stringify(newMatches));

    // Aggiorna anche il bracket (se esiste)
    try {
      let bracket = JSON.parse(localStorage.getItem('bracket') || 'null');
      if (bracket) {
        // Aggiorna i nomi delle squadre nel bracket
        let bracketChanged = false;
        for (let round of bracket) {
          for (let match of round) {
            if (match.team1 && teams[match.team1.id]) {
              match.team1.name = teams[match.team1.id].name;
              bracketChanged = true;
            }
            if (match.team2 && teams[match.team2.id]) {
              match.team2.name = teams[match.team2.id].name;
              bracketChanged = true;
            }
          }
        }
        if (bracketChanged) {
          localStorage.setItem('bracket', JSON.stringify(bracket));
        }
      }
    } catch (e) {
      console.error('Errore nell\'aggiornamento bracket', e);
    }
  }, []);

  return (
    <View style={styles.container}>
      <ScrollView style={styles.container}>
        {/* Titolo Squadre con icona */}
        <View style={{ alignItems: 'center', marginTop: 16, marginBottom: 2 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
            <UsersIcon style={{ fontSize: 36, color: theme.primary, marginRight: 12 }} />
            <Text style={{ fontSize: 32, fontWeight: 'bold', color: theme.primary, margin: 0, textAlign: 'center', letterSpacing: 1, textShadow: '0px 2px 4px rgba(0,0,0,0.06)' }}>Squadre</Text>
            <FencingSwordsIcon style={{ fontSize: 36, color: theme.primary, marginLeft: 12 }} />
          </View>
          <View style={{ height: 4, backgroundColor: theme.primary, width: 72, borderRadius: 2, alignSelf: 'center', marginTop: 2, marginBottom: 18, opacity: 0.2 }} />
        </View>

        {/* BOX IMPOSTAZIONI GENERALI */}
        <View style={styles.card}>
          <Text style={styles.title}>
            <CustomSettingsIcon style={{ fontSize: 24, color: theme.primary, marginRight: 8 }} /> Impostazioni Generali
          </Text>
          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 4, color: theme.primary }}>Numero Squadre</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', maxWidth: '100%', overflow: 'hidden' }}>
              <TextInput
                style={{ ...styles.input, flex: 1, minWidth: 0, maxWidth: '100%', flexShrink: 1, marginRight: 4, fontSize: 15, padding: 8 }}
                value={String(numTeams)}
                onChangeText={handleNumTeamsChange}
                keyboardType="numeric"
                placeholder="N"
              />

              <TouchableOpacity
                style={{ ...styles.button, ...styles.btnPrimary, paddingVertical: 6, paddingHorizontal: 12, marginRight: 4 }}
                onPress={() => handleNumTeamsChange(Math.max(2, numTeams - 1).toString())}
              >
                <Text style={styles.btnText}>-</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={{ ...styles.button, ...styles.btnPrimary, paddingVertical: 6, paddingHorizontal: 12 }}
                onPress={() => handleNumTeamsChange((numTeams + 1).toString())}
              >
                <Text style={styles.btnText}>+</Text>
              </TouchableOpacity>
            </View>
          </View>
          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 4, color: theme.primary }}>Stoccate per assalto</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', maxWidth: '100%', overflow: 'hidden' }}>
              <TextInput
                style={{ ...styles.input, flex: 1, minWidth: 0, maxWidth: '100%', flexShrink: 1, marginRight: 4, fontSize: 15, padding: 8 }}
                value={String(stoccatePerAssalto)}
                onChangeText={setStoccatePerAssalto}
                keyboardType="numeric"
                placeholder="5"
              />

              <TouchableOpacity
                style={{ ...styles.button, ...styles.btnPrimary, paddingVertical: 6, paddingHorizontal: 12, marginRight: 4 }}
                onPress={() => decrement(setStoccatePerAssalto, stoccatePerAssalto, 1)}
              >
                <Text style={styles.btnText}>-</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={{ ...styles.button, ...styles.btnPrimary, paddingVertical: 6, paddingHorizontal: 12 }}
                onPress={() => increment(setStoccatePerAssalto, stoccatePerAssalto)}
              >
                <Text style={styles.btnText}>+</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>


        {/* LISTA SQUADRE */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', padding: 0, margin: 0 }}>
          {teams.map((team, teamIdx) => (
            <View key={teamIdx} style={{
              ...styles.card,
              flex: 1,
              minWidth: 280,
              marginVertical: 12,
              flexDirection: 'column',
              alignItems: 'stretch',
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10, marginBottom: 10 }}>
                <TextInput
                  style={{ flex: 1, borderWidth: 2, borderColor: '#b3c6e6', borderRadius: 8, padding: 8, fontSize: 15, backgroundColor: '#f4faff', fontWeight: 'bold', transition: 'border-color 0.2s', minWidth: 0, maxWidth: '100%', flexShrink: 1 }}
                  value={team.name}
                  onChangeText={val => updateTeamName(teamIdx, val)}
                  placeholder={`Nome squadra ${teamIdx + 1}`}
                />
                <TouchableOpacity
                  style={{
                    marginLeft: 8,
                    width: 36,
                    height: 36,
                    minWidth: 36,
                    borderRadius: 18,
                    backgroundColor: '#e3f2fd',
                    borderWidth: 2,
                    borderColor: '#b3c6e6',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'border-color 0.2s',
                  }}
                  onPress={() => addMember(teamIdx)}
                  title="Aggiungi membro"
                >
                  <UserPlusIcon size={24} color={'#1976d2'} />
                </TouchableOpacity>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'baseline', marginBottom: 8 }}>
                <Text style={{ fontWeight: 'bold', color: '#388e3c', fontSize: 15 }}>Membri:</Text>
                <Text style={{ fontSize: 14, color: '#1976d2', marginLeft: 8 }}>(L'ultimo chiude!)</Text>
              </View>
              <DraggableList
                items={team.members}
                onMove={(from, to) => {
                  const members = [...team.members];
                  const [removed] = members.splice(from, 1);
                  members.splice(to, 0, removed);
                  const updated = [...teams];
                  updated[teamIdx].members = members;
                  setTeams(updated);
                  localStorage.setItem('teams', JSON.stringify(updated));
                }}
                renderItem={(member, mIdx, isDragging, dragHandleProps) => (
                  <View {...dragHandleProps} style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: mIdx % 2 === 0 ? '#f7fafc' : '#e3f2fd',
                    borderRadius: 10,
                    marginBottom: 10,
                    padding: 10,
                    borderWidth: 2,
                    borderColor: isDragging ? '#1976d2' : '#e0e3ef',
                    transition: 'background 0.2s',
                    flexWrap: 'nowrap',
                    maxWidth: '100%',
                    overflow: 'hidden',
                  }}>
                    <TouchableOpacity style={{ width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', marginRight: 10, backgroundColor: '#fff', borderWidth: 2, borderColor: '#b3c6e6', transition: 'border-color 0.2s' }} onPress={() => removeMember(teamIdx, mIdx)}>
                      <UserMinusIcon size={24} color="#d32f2f" />
                    </TouchableOpacity>
                    <TextInput
                      style={{ borderWidth: 2, borderColor: '#b3c6e6', borderRadius: 8, padding: 8, fontSize: 15, marginRight: 4, backgroundColor: '#f4faff', fontWeight: 'bold', flex: 1, minWidth: 0, maxWidth: '60vw', flexShrink: 1, transition: 'border-color 0.2s' }}
                      value={member.name}
                      onChangeText={val => updateTeamMember(teamIdx, mIdx, val)}
                      placeholder={`Tiratore ${mIdx + 1}`}
                    />
                  </View>
                )}
              />

            </View>
          ))}
        </View>

        {/* --- PULSANTI DI AZIONE --- */}
        <View style={{ padding: '0 24px' }}>
          {/* --- CREA TORNEO --- */}
          <View className="button-container">
            <TouchableOpacity
              style={{ ...styles.button, ...styles.btnSecondary }}
              className="common-button success"
              onPress={() => {
                const matches = generateRoundRobinMatches(teams);
                saveTeams(teams);
                saveMatches(matches);
                console.log('MATCHES:', matches);
                navigate('/schedule', { state: { teams, matches } });
              }}
            >
              <Text style={styles.btnText}>Crea Torneo</Text>
            </TouchableOpacity>
          </View>

          {/* --- PULSANTE AGGIORNA DATI TORNEO --- */}
          <View className="button-container">
            <TouchableOpacity
              style={{ ...styles.button, ...styles.btnPrimary }}
              className="common-button"
              onPress={() => {
                aggiornaDatiTorneo(teams, stoccatePerAssalto);
                alert('Dati torneo aggiornati: Modifiche applicate preservando i risultati già inseriti!');
              }}
            >
              <Text style={styles.btnText}>Aggiorna dati torneo</Text>
            </TouchableOpacity>
            <Text style={{ fontSize: 12, color: '#666', textAlign: 'center', fontStyle: 'italic', paddingHorizontal: 8 }}>
              Ora puoi modificare i membri delle squadre e il loro ordine mantenendo i risultati già inseriti degli assalti.
            </Text>
          </View>
        </View>
      </ScrollView>
      <ScrollToTopButton threshold={200} />
    </View>
  );
};

export default SetupWizard;